import { useParams, Link, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Building2, Factory, Home, Hammer, Sparkles, SprayCan,
  CheckCircle, ArrowRight, Phone, Clock, Shield, Award, ArrowLeft
} from "lucide-react";
import SEO from "../components/SEO";
import { useLanguage } from "../contexts/LanguageContext";

const ServiceDetailPage = () => {
  const { serviceSlug } = useParams();
  const { t } = useLanguage();

  const servicesData = {
    "nettoyage-bureaux": {
      icon: Building2,
      title: "Nettoyage de bureaux",
      subtitle: "Des espaces de travail impeccables pour vos équipes",
      description: "Un environnement de travail propre et sain améliore la productivité et le bien-être de vos collaborateurs. Nettis Facility assure l'entretien complet de vos bureaux avec des équipes formées et des produits professionnels.",
      longDescription: "Le nettoyage de bureaux est notre spécialité. Nous intervenons quotidiennement, hebdomadairement ou selon vos besoins pour maintenir vos locaux professionnels dans un état impeccable. Nos équipes sont formées aux dernières techniques de nettoyage et utilisent des produits écologiques certifiés.",
      image: "https://images.unsplash.com/photo-1637095937545-7d8c1edf4d2b?auto=format&fit=crop&w=800&q=80",
      features: [
        "Nettoyage quotidien des postes de travail",
        "Aspiration et lavage des sols",
        "Nettoyage des sanitaires et cuisines",
        "Vidage des poubelles et tri sélectif",
        "Dépoussiérage du mobilier",
        "Nettoyage des vitres intérieures"
      ],
      benefits: [
        { icon: Clock, title: "Flexibilité horaire", desc: "Intervention en dehors des heures de bureau" },
        { icon: Shield, title: "Personnel vérifié", desc: "Équipes de confiance et assurées" },
        { icon: Award, title: "Qualité garantie", desc: "Contrôles réguliers et suivi personnalisé" }
      ],
      seoKeywords: "nettoyage bureaux Bruxelles, entretien bureaux professionnels, société nettoyage bureaux Belgique, nettoyage locaux entreprise",
      seoDescription: "Service de nettoyage de bureaux professionnel à Bruxelles et en Belgique. Entretien quotidien, hebdomadaire ou mensuel. Devis gratuit, équipes formées, produits écologiques."
    },
    "nettoyage-industriel": {
      icon: Factory,
      title: "Nettoyage industriel",
      subtitle: "Solutions adaptées aux environnements industriels exigeants",
      description: "Les environnements industriels nécessitent une expertise spécifique. Nettis Facility dispose des équipements et du savoir-faire pour nettoyer usines, entrepôts et sites de production dans le respect des normes de sécurité.",
      longDescription: "Notre service de nettoyage industriel répond aux exigences les plus strictes. Nous intervenons dans les usines, entrepôts, ateliers de production et sites logistiques avec des équipements professionnels adaptés. Nos équipes sont formées aux normes de sécurité industrielle.",
      image: "https://images.pexels.com/photos/5759145/pexels-photo-5759145.jpeg?auto=compress&cs=tinysrgb&w=800",
      features: [
        "Nettoyage de sols industriels (béton, résine, carrelage)",
        "Dégraissage de machines et équipements",
        "Nettoyage haute pression",
        "Aspiration industrielle",
        "Nettoyage de lignes de production",
        "Gestion des déchets industriels"
      ],
      benefits: [
        { icon: Shield, title: "Normes respectées", desc: "Conformité HACCP, ISO et autres normes" },
        { icon: Clock, title: "Intervention 24/7", desc: "Disponibilité selon vos cycles de production" },
        { icon: Award, title: "Équipements pro", desc: "Machines industrielles performantes" }
      ],
      seoKeywords: "nettoyage industriel Belgique, nettoyage usine, entretien entrepôt, nettoyage site production, dégraissage industriel",
      seoDescription: "Nettoyage industriel professionnel en Belgique. Usines, entrepôts, sites de production. Équipements spécialisés, normes de sécurité respectées. Devis gratuit."
    },
    "entretien-immeubles": {
      icon: Home,
      title: "Entretien d'immeubles",
      subtitle: "Des parties communes toujours propres et accueillantes",
      description: "L'entretien régulier des parties communes d'un immeuble contribue à la qualité de vie des résidents et à la valorisation du patrimoine immobilier. Nettis Facility propose des contrats adaptés aux syndics et copropriétés.",
      longDescription: "Nous assurons l'entretien complet des immeubles résidentiels et de bureaux : halls d'entrée, escaliers, ascenseurs, caves, parkings et espaces verts. Nos contrats sont flexibles et adaptés à chaque copropriété.",
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80",
      features: [
        "Nettoyage des halls d'entrée",
        "Entretien des escaliers et paliers",
        "Nettoyage des ascenseurs",
        "Entretien des caves et parkings",
        "Lavage des vitres communes",
        "Entretien des espaces verts"
      ],
      benefits: [
        { icon: Clock, title: "Régularité", desc: "Planning d'intervention fixe et respecté" },
        { icon: Shield, title: "Confiance", desc: "Personnel stable et connu des résidents" },
        { icon: Award, title: "Suivi syndic", desc: "Rapports réguliers et cahier de liaison" }
      ],
      seoKeywords: "entretien immeuble Bruxelles, nettoyage copropriété, entretien parties communes, nettoyage syndic, entretien résidence",
      seoDescription: "Entretien d'immeubles et copropriétés à Bruxelles et en Belgique. Parties communes, halls, escaliers, parkings. Contrats adaptés aux syndics. Devis gratuit."
    },
    "nettoyage-apres-travaux": {
      icon: Hammer,
      title: "Nettoyage après travaux",
      subtitle: "Remise en état complète après chantier",
      description: "Après des travaux de construction ou de rénovation, un nettoyage spécialisé est indispensable. Nettis Facility intervient pour éliminer poussières, résidus et traces de chantier afin de vous livrer des locaux prêts à l'emploi.",
      longDescription: "Notre service de nettoyage après travaux comprend l'élimination de toutes les traces de chantier : poussières de plâtre, résidus de peinture, joints de carrelage, étiquettes et protections. Nous rendons vos locaux impeccables et prêts à être utilisés.",
      image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=800&q=80",
      features: [
        "Élimination des poussières de chantier",
        "Nettoyage des traces de peinture et plâtre",
        "Décapage des sols",
        "Nettoyage des vitres et châssis",
        "Enlèvement des étiquettes et protections",
        "Désinfection complète"
      ],
      benefits: [
        { icon: Clock, title: "Rapidité", desc: "Intervention rapide pour respecter vos délais" },
        { icon: Shield, title: "Efficacité", desc: "Équipements adaptés au nettoyage de chantier" },
        { icon: Award, title: "Clé en main", desc: "Locaux prêts à être utilisés" }
      ],
      seoKeywords: "nettoyage après travaux Bruxelles, nettoyage fin de chantier, remise en état après rénovation, nettoyage post-construction",
      seoDescription: "Nettoyage après travaux et fin de chantier à Bruxelles et en Belgique. Élimination poussières, traces de peinture, remise en état complète. Devis gratuit."
    },
    "desinfection": {
      icon: SprayCan,
      title: "Désinfection professionnelle",
      subtitle: "Protection sanitaire de vos espaces",
      description: "La désinfection professionnelle est essentielle pour garantir un environnement sain. Nettis Facility utilise des produits virucides et bactéricides certifiés pour éliminer les agents pathogènes de vos locaux.",
      longDescription: "Nos services de désinfection répondent aux normes sanitaires les plus strictes. Nous utilisons des produits certifiés et des techniques éprouvées (nébulisation, pulvérisation) pour traiter efficacement tous types de locaux.",
      image: "https://images.pexels.com/photos/6812463/pexels-photo-6812463.jpeg?auto=compress&cs=tinysrgb&w=800",
      features: [
        "Désinfection par nébulisation",
        "Traitement des surfaces de contact",
        "Désinfection de véhicules",
        "Protocoles COVID et autres virus",
        "Traitement anti-bactérien",
        "Certification des interventions"
      ],
      benefits: [
        { icon: Shield, title: "Produits certifiés", desc: "Virucides et bactéricides homologués" },
        { icon: Clock, title: "Intervention rapide", desc: "Disponibilité en urgence" },
        { icon: Award, title: "Attestation", desc: "Certificat de désinfection fourni" }
      ],
      seoKeywords: "désinfection professionnelle Bruxelles, désinfection locaux entreprise, traitement sanitaire, désinfection COVID Belgique",
      seoDescription: "Désinfection professionnelle à Bruxelles et en Belgique. Produits certifiés, nébulisation, traitement anti-viral et anti-bactérien. Intervention rapide, certificat fourni."
    },
    "nettoyage-vitres": {
      icon: Sparkles,
      title: "Nettoyage de vitres",
      subtitle: "Des vitres impeccables pour une image professionnelle",
      description: "Des vitres propres améliorent la luminosité et l'image de vos locaux. Nettis Facility assure le nettoyage intérieur et extérieur de toutes vos surfaces vitrées, même en hauteur.",
      longDescription: "Notre service de nettoyage de vitres couvre tous types de surfaces vitrées : fenêtres, baies vitrées, verrières, vitrines commerciales. Nous intervenons en intérieur comme en extérieur, y compris pour les travaux en hauteur avec nacelle.",
      image: "https://images.unsplash.com/photo-1632927063333-df848f5f916a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHx3aW5kb3clMjBjbGVhbmVyfGVufDB8fHx8MTc3OTM1NzUwMnww&ixlib=rb-4.1.0&q=85",
      features: [
        "Nettoyage vitres intérieures et extérieures",
        "Lavage de vitrines commerciales",
        "Nettoyage de verrières et toits vitrés",
        "Intervention en hauteur (nacelle)",
        "Nettoyage de châssis et encadrements",
        "Traitement anti-calcaire"
      ],
      benefits: [
        { icon: Clock, title: "Régularité", desc: "Contrats d'entretien périodique" },
        { icon: Shield, title: "Sécurité", desc: "Personnel formé aux travaux en hauteur" },
        { icon: Award, title: "Résultat parfait", desc: "Sans traces ni coulures" }
      ],
      seoKeywords: "nettoyage vitres Bruxelles, lavage vitres entreprise, nettoyage vitrines, laveur de vitres professionnel Belgique",
      seoDescription: "Nettoyage de vitres professionnel à Bruxelles et en Belgique. Intérieur, extérieur, hauteur. Vitrines, baies vitrées, verrières. Devis gratuit."
    }
  };

  const service = servicesData[serviceSlug];

  // Redirect to services page if service not found
  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const Icon = service.icon;

  const otherServices = Object.entries(servicesData)
    .filter(([slug]) => slug !== serviceSlug)
    .slice(0, 3);

  return (
    <div className="pt-20">
      <SEO
        title={service.title}
        description={service.seoDescription}
        keywords={service.seoKeywords}
        canonicalUrl={`https://www.nettis.be/services/${serviceSlug}`}
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <Link to="/services" className="inline-flex items-center gap-2 text-[#1E3A8A] hover:underline mb-6">
            <ArrowLeft className="w-4 h-4" /> Retour aux services
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 rounded-2xl bg-[#1E3A8A]/10 flex items-center justify-center mb-6">
                <Icon className="w-8 h-8 text-[#1E3A8A]" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4">
                {service.title}
              </h1>
              <p className="text-xl text-[#10B981] font-medium mb-6">{service.subtitle}</p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {service.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-3">
                    Demander un devis gratuit <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:+3228806650">
                  <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A]/5 rounded-full px-8 py-3">
                    <Phone className="mr-2 w-4 h-4" /> +32 2 880 66 50
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src={service.image} 
                alt={service.title}
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#10B981]/10 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A]">Devis gratuit</p>
                    <p className="text-sm text-slate-500">Sans engagement</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-6">
                Ce que comprend notre service
              </h2>
              <p className="text-slate-600 mb-8">{service.longDescription}</p>
              <div className="space-y-4">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Nos garanties</h3>
              {service.benefits.map((benefit, index) => {
                const BenefitIcon = benefit.icon;
                return (
                  <div key={index} className="flex items-start gap-4 p-6 bg-slate-50 rounded-xl">
                    <div className="w-12 h-12 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                      <BenefitIcon className="w-6 h-6 text-[#1E3A8A]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#0F172A] mb-1">{benefit.title}</h4>
                      <p className="text-slate-600 text-sm">{benefit.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#1E3A8A] to-[#0F172A]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Besoin d'un devis pour {service.title.toLowerCase()} ?
              </h2>
              <p className="text-blue-100">
                Contactez-nous pour une estimation gratuite et personnalisée. Nos équipes sont disponibles pour répondre à toutes vos questions.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <Link to="/contact">
                <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-3">
                  Demander un devis
                </Button>
              </Link>
              <a href="tel:+3228806650">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3">
                  <Phone className="mr-2 w-4 h-4" /> Nous appeler
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-8 text-center">
            Découvrez nos autres services
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {otherServices.map(([slug, otherService]) => {
              const OtherIcon = otherService.icon;
              return (
                <Link 
                  key={slug} 
                  to={`/services/${slug}`}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow group"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center mb-4 group-hover:bg-[#1E3A8A]/20 transition-colors">
                    <OtherIcon className="w-6 h-6 text-[#1E3A8A]" />
                  </div>
                  <h3 className="font-semibold text-[#0F172A] mb-2">{otherService.title}</h3>
                  <p className="text-sm text-slate-600 line-clamp-2">{otherService.subtitle}</p>
                  <span className="inline-flex items-center gap-1 text-[#10B981] text-sm font-medium mt-4 group-hover:gap-2 transition-all">
                    En savoir plus <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
